/*
 * Product page: keeps the buy button reachable when the info panel is taller
 * than the window (sections/main-product*.liquid).
 *
 * 1. Two-way sticky panel. A `.product__info.sticky` panel taller than the
 *    space below the header used to stick at the header offset, so its lower
 *    part (with the buy button) stayed below the window until the gallery
 *    ended. Now such a panel moves with the page: scrolling down it scrolls
 *    until its bottom is in view and then stays; scrolling up it scrolls back
 *    until its top (the title) is in view and then stays. Short panels keep
 *    the theme's normal sticky behaviour.
 * 2. Long product description is clamped to a few lines with "Show more".
 */
(() => {
  const DESKTOP = 1024;
  const BOTTOM_GAP = 20;

  const initPanel = (panel) => {
    if (panel.dataset.stickyReady) return;
    panel.dataset.stickyReady = "true";

    let tall = false;
    let maxTop = 0;
    let minTop = 0;
    let top = 0;
    let lastY = window.scrollY;

    const apply = () => {
      panel.style.setProperty("inset-block-start", `${Math.round(top)}px`, "important");
    };

    // Re-read the theme's sticky offset and the panel height.
    // The theme animates `top`; measuring and scroll-driven moves must not
    // trigger that animation, so it stays off while the panel is tall.
    const measure = () => {
      panel.style.transition = "none";
      panel.style.removeProperty("inset-block-start");
      tall = false;

      if (window.innerWidth >= DESKTOP) {
        maxTop = parseFloat(getComputedStyle(panel).top) || 0;
        minTop = window.innerHeight - panel.offsetHeight - BOTTOM_GAP;
        tall = minTop < maxTop;
      }

      if (tall) {
        top = Math.min(maxTop, Math.max(minTop, top || maxTop));
        apply();
      } else {
        getComputedStyle(panel).top; // flush before restoring the transition
        panel.style.removeProperty("transition");
      }
    };

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      lastY = y;
      if (!tall || delta === 0) return;
      const next = Math.min(maxTop, Math.max(minTop, top - delta));
      if (next !== top) {
        top = next;
        apply();
      }
    };

    new ResizeObserver(measure).observe(panel);
    window.addEventListener("resize", measure, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    measure();
  };

  const initDescription = (wrapper) => {
    if (wrapper.dataset.clampReady) return;
    wrapper.dataset.clampReady = "true";

    const content = wrapper.querySelector("[data-description-content]");
    const toggle = wrapper.querySelector("[data-description-toggle]");
    if (!content || !toggle) return;

    const collapsedHeight = parseFloat(getComputedStyle(content).maxHeight);
    // Only clamp when it hides a meaningful amount of text.
    if (!collapsedHeight || content.scrollHeight <= collapsedHeight * 1.35) {
      wrapper.classList.remove("is-clamped");
      return;
    }

    toggle.hidden = false;
    toggle.addEventListener("click", () => {
      wrapper.classList.remove("is-clamped");
      toggle.setAttribute("aria-expanded", "true");
      toggle.hidden = true;
      content.focus({ preventScroll: true });
    });
  };

  const init = (root = document) => {
    root.querySelectorAll(".product__info.sticky").forEach(initPanel);
    root.querySelectorAll("[data-product-description].is-clamped").forEach(initDescription);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
